import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useCommunicationActivity } from "../hooks/useCommunications";
import { relativeTime } from "@/components/ux/relativeTime";
import type { CommunicationLogActivityDto } from "@/api/communications";

interface Props {
  entryId: string;
}

const ACTIVITY_LABEL: Record<
  CommunicationLogActivityDto["activityType"],
  string
> = {
  Created: "Logged",
  EditedField: "Edited",
  PlacementAdded: "Placement added",
  PlacementRemoved: "Placement removed",
  ParticipantAdded: "Participant added",
  ParticipantRemoved: "Participant removed",
  SoftDeleted: "Deleted",
  Restored: "Restored",
};

export function EntryActivityLog({ entryId }: Props) {
  const [expanded, setExpanded] = useState(false);
  const q = useCommunicationActivity(expanded ? entryId : undefined);

  return (
    <section>
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex items-center gap-space-1 font-display text-label font-semibold uppercase tracking-label text-gold-shadow hover:text-gold"
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown size={14} aria-hidden />
        ) : (
          <ChevronRight size={14} aria-hidden />
        )}
        Activity
      </button>
      {expanded && (
        <div className="mt-space-3">
          {q.isLoading && (
            <p className="text-small text-gray-mid m-0">Loading activity…</p>
          )}
          {q.isError && (
            <p className="text-small text-status-error m-0">
              Could not load activity.
            </p>
          )}
          {q.data && q.data.data.length === 0 && (
            <p className="text-small text-gray-mid m-0">No activity yet.</p>
          )}
          {q.data && q.data.data.length > 0 && (
            <ul className="flex flex-col gap-space-2 m-0 p-0 list-none">
              {q.data.data.map((a) => (
                <li
                  key={a.id}
                  className="flex items-start gap-space-2 text-small"
                >
                  <span className="font-display text-[10px] font-bold uppercase tracking-[0.12em] text-gold-shadow shrink-0 mt-[3px] w-32">
                    {ACTIVITY_LABEL[a.activityType] ?? a.activityType}
                  </span>
                  <span className="text-onyx flex-1 min-w-0">
                    <span className="font-semibold">{a.actorDisplayName}</span>
                    {a.fieldName && (
                      <>
                        {" "}changed <em>{a.fieldName}</em>
                      </>
                    )}
                    {a.placementOwnerType && (
                      <>
                        {" "}({a.placementOwnerType}:{a.placementOwnerId})
                      </>
                    )}
                  </span>
                  <span className="text-label text-gray-mid shrink-0 tabular-nums">
                    {relativeTime(a.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
